import React, { useState, useEffect } from 'react';
import { Layers, Download, RotateCw, Trash2, Copy, ArrowUp, ArrowDown, FileText } from 'lucide-react';
import { Button, Card, FileUploadZone, ProgressBar, Alert, Badge, PDFPreview, ToolActionButtons } from '../shared';
import { PDFDocument, degrees } from 'pdf-lib';
import { saveAs } from 'file-saver';
import { toast } from 'react-toastify';
import '../../styles/design-system.css';

const OrganizePDF = () => {
    const [file, setFile] = useState(null);
    const [pdfDoc, setPdfDoc] = useState(null);
    const [pages, setPages] = useState([]);
    const [selectedPages, setSelectedPages] = useState([]);
    const [isProcessing, setIsProcessing] = useState(false);
    const [progress, setProgress] = useState(0);
    const [previewBlob, setPreviewBlob] = useState(null);
    const [previewFilename, setPreviewFilename] = useState('');
    const [showPreview, setShowPreview] = useState(false);

    const handleFileSelected = async (files) => {
        if (files && files.length > 0) {
            const selectedFile = files[0];
            setFile(selectedFile);

            try {
                const arrayBuffer = await selectedFile.arrayBuffer();
                const pdf = await PDFDocument.load(arrayBuffer);
                setPdfDoc(pdf);

                // Initialize pages array with metadata
                const pageCount = pdf.getPageCount();
                const pagesArray = Array.from({ length: pageCount }, (_, i) => ({
                    id: `page-${i}-${Date.now()}`,
                    originalIndex: i,
                    currentIndex: i,
                    rotation: 0,
                    pageNumber: i + 1
                }));
                setPages(pagesArray);
                setSelectedPages([]);

                toast.success(`PDF loaded: ${pageCount} pages`);
            } catch (error) {
                toast.error(`Failed to load PDF: ${error.message}`);
            }
        }
    };

    const togglePageSelection = (pageId) => {
        setSelectedPages(prev => {
            if (prev.includes(pageId)) {
                return prev.filter(id => id !== pageId);
            } else {
                return [...prev, pageId];
            }
        });
    };

    const sortPages = (sortType) => {
        setPages(prev => {
            const sorted = [...prev];
            switch (sortType) {
                case 'name-asc':
                case 'page-asc':
                    return sorted.sort((a, b) => a.pageNumber - b.pageNumber);
                case 'name-desc':
                case 'page-desc':
                    return sorted.sort((a, b) => b.pageNumber - a.pageNumber);
                default:
                    return sorted;
            }
        });
        toast.success('Pages sorted successfully');
    };

    const selectAllPages = () => {
        setSelectedPages(pages.map(p => p.id));
    };

    const deselectAllPages = () => {
        setSelectedPages([]);
    };

    // Rotate selected pages
    const rotatePages = (angle) => {
        if (selectedPages.length === 0) {
            toast.warning('Please select pages to rotate');
            return;
        }

        setPages(prev => prev.map(page => {
            if (selectedPages.includes(page.id)) {
                return { ...page, rotation: (page.rotation + angle) % 360 };
            }
            return page;
        }));

        toast.success(`Rotated ${selectedPages.length} page(s) by ${angle}°`);
    };

    // Delete selected pages
    const deletePages = () => {
        if (selectedPages.length === 0) {
            toast.warning('Please select pages to delete');
            return;
        }

        if (selectedPages.length === pages.length) {
            toast.error('Cannot delete all pages');
            return;
        }

        setPages(prev => prev.filter(page => !selectedPages.includes(page.id)));
        setSelectedPages([]);
        toast.success(`Deleted ${selectedPages.length} page(s)`);
    };

    // Duplicate selected pages
    const duplicatePages = () => {
        if (selectedPages.length === 0) {
            toast.warning('Please select pages to duplicate');
            return;
        }

        const newPages = [];
        pages.forEach(page => {
            newPages.push(page);
            if (selectedPages.includes(page.id)) {
                // Create duplicate
                newPages.push({
                    ...page,
                    id: `page-${page.originalIndex}-${Date.now()}-${Math.random()}`,
                    isDuplicate: true
                });
            }
        });

        setPages(newPages);
        setSelectedPages([]);
        toast.success(`Duplicated ${selectedPages.length} page(s)`);
    };

    // Move page up
    const movePageUp = (index) => {
        if (index === 0) return;
        const newPages = [...pages];
        [newPages[index - 1], newPages[index]] = [newPages[index], newPages[index - 1]];
        setPages(newPages);
    };

    // Move page down
    const movePageDown = (index) => {
        if (index === pages.length - 1) return;
        const newPages = [...pages];
        [newPages[index], newPages[index + 1]] = [newPages[index + 1], newPages[index]];
        setPages(newPages);
    };

    // Drag and drop handlers
    const handleDragStart = (e, index) => {
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/html', index.toString());
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
    };

    const handleDrop = (e, dropIndex) => {
        e.preventDefault();
        const dragIndex = parseInt(e.dataTransfer.getData('text/html'));

        if (dragIndex === dropIndex) return;

        const newPages = [...pages];
        const draggedPage = newPages[dragIndex];
        newPages.splice(dragIndex, 1);
        newPages.splice(dropIndex, 0, draggedPage);

        setPages(newPages);
        toast.success('Page reordered');
    };

    // Apply changes and save
    const applyChanges = async () => {
        if (!pdfDoc || pages.length === 0) return;

        try {
            setIsProcessing(true);
            setProgress(10);

            const newPdf = await PDFDocument.create();
            setProgress(30);

            // Process each page in the new order
            for (let i = 0; i < pages.length; i++) {
                const page = pages[i];
                setProgress(30 + ((i + 1) / pages.length) * 60);

                const [copiedPage] = await newPdf.copyPages(pdfDoc, [page.originalIndex]);

                // Apply rotation
                if (page.rotation !== 0) {
                    copiedPage.setRotation(degrees(page.rotation));
                }

                newPdf.addPage(copiedPage);
            }

            setProgress(95);

            const pdfBytes = await newPdf.save();
            const blob = new Blob([pdfBytes], { type: 'application/pdf' });

            setProgress(100);

            const timestamp = new Date().toISOString().slice(0, 10);
            const filename = `organized-${file.name}`;

            // Set preview
            setPreviewBlob(blob);
            setPreviewFilename(filename);

            toast.success('PDF organized! Opening preview...');

            setTimeout(() => {
                setProgress(0);
            }, 1000);

        } catch (error) {
            console.error('Organize error:', error);
            toast.error(`Failed to organize PDF: ${error.message}`);
        } finally {
            setIsProcessing(false);
        }
    };

    // Generate live preview when pages are reorganized
    const generateLivePreview = async () => {
        if (!pdfDoc || pages.length === 0) {
            setPreviewBlob(null);
            return;
        }

        try {
            const newPdf = await PDFDocument.create();

            for (const page of pages) {
                const [copiedPage] = await newPdf.copyPages(pdfDoc, [page.originalIndex]);
                if (page.rotation !== 0) {
                    copiedPage.setRotation(degrees(page.rotation));
                }
                newPdf.addPage(copiedPage);
            }

            const pdfBytes = await newPdf.save();
            const blob = new Blob([pdfBytes], { type: 'application/pdf' });
            const filename = `organized-${file.name}`;
            
            setPreviewBlob(blob);
            setPreviewFilename(filename);
        } catch (error) {
            console.error('Preview generation error:', error);
            setPreviewBlob(null);
        }
    };

    // Auto-update preview when pages change
    useEffect(() => {
        if (file && pdfDoc && pages.length > 0) {
            const timeoutId = setTimeout(() => {
                generateLivePreview();
            }, 500);
            return () => clearTimeout(timeoutId);
        }
    }, [pages, file, pdfDoc]);

    return (
        <div>
            {/* Action Buttons */}
            <ToolActionButtons
                onAdd={() => {
                    document.querySelector('input[type="file"]')?.click();
                }}
                onSort={(sortType) => {
                    if (sortType && pages.length > 0) {
                        sortPages(sortType);
                    }
                }}
                notificationCount={pages.length}
            />

            {/* Header */}
            <div className="tool-header">
                <h1 className="tool-title">
                    <Layers size={36} style={{ display: 'inline', marginRight: 'var(--spacing-3)', color: 'var(--color-accent)' }} />
                    Organize PDF
                </h1>
                <p className="tool-description">
                    Reorder, rotate, delete, and duplicate PDF pages. Drag and drop to rearrange pages in any order.
                </p>
            </div>

            {/* File Upload */}
            {!file ? (
                <FileUploadZone
                    onFilesSelected={handleFileSelected}
                    acceptedTypes={['application/pdf']}
                    multiple={false}
                    title="Drop PDF file here"
                    description="or click to browse"
                />
            ) : (
                <Alert variant="success" title="PDF Loaded">
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)' }}>
                            <FileText size={16} />
                            <span>{file.name}</span>
                            <Badge variant="info">{pages.length} pages</Badge>
                        </div>
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                                setFile(null);
                                setPdfDoc(null);
                                setPages([]);
                                setSelectedPages([]);
                            }}
                        >
                            Change PDF
                        </Button>
                    </div>
                </Alert>
            )}

            {file && pages.length > 0 && (
                <>
                    {/* Toolbar */}
                    <div style={{ marginTop: 'var(--spacing-6)' }}>
                        <Card>
                            <h3 style={{
                                fontSize: 'var(--text-lg)',
                                fontWeight: 'var(--font-bold)',
                                marginBottom: 'var(--spacing-4)'
                            }}>
                                Page Actions
                            </h3>

                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--spacing-3)', marginBottom: 'var(--spacing-4)' }}>
                                <Button variant="secondary" size="sm" onClick={() => rotatePages(90)}>
                                    <RotateCw size={16} />
                                    Rotate 90°
                                </Button>
                                <Button variant="secondary" size="sm" onClick={() => rotatePages(180)}>
                                    <RotateCw size={16} />
                                    Rotate 180°
                                </Button>
                                <Button variant="secondary" size="sm" onClick={duplicatePages}>
                                    <Copy size={16} />
                                    Duplicate
                                </Button>
                                <Button variant="danger" size="sm" onClick={deletePages}>
                                    <Trash2 size={16} />
                                    Delete
                                </Button>
                            </div>

                            <div style={{ display: 'flex', gap: 'var(--spacing-2)' }}>
                                <Button variant="ghost" size="sm" onClick={selectAllPages}>
                                    Select All
                                </Button>
                                <Button variant="ghost" size="sm" onClick={deselectAllPages}>
                                    Deselect All
                                </Button>
                                <Badge variant="info">{selectedPages.length} selected</Badge>
                            </div>
                        </Card>
                    </div>

                    {/* Pages Grid */}
                    <Card>
                        <Alert variant="info">
                            <strong>Tip:</strong> Drag and drop pages to reorder them. Click to select, then use actions above.
                        </Alert>

                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
                                gap: 'var(--spacing-4)'
                            }}>
                                {pages.map((page, index) => {
                                    const isSelected = selectedPages.includes(page.id);

                                    return (
                                        <div
                                            key={page.id}
                                            draggable
                                            onDragStart={(e) => handleDragStart(e, index)}
                                            onDragOver={handleDragOver}
                                            onDrop={(e) => handleDrop(e, index)}
                                            onClick={() => togglePageSelection(page.id)}
                                            style={{
                                                cursor: 'move',
                                                transition: 'all var(--transition-base)',
                                                position: 'relative'
                                            }}
                                        >
                                            <Card
                                                variant={isSelected ? 'elevated' : 'outlined'}
                                                style={{
                                                    border: isSelected
                                                        ? '2px solid var(--color-accent)'
                                                        : '1px solid var(--color-border)',
                                                    backgroundColor: isSelected
                                                        ? 'var(--color-accent-subtle)'
                                                        : 'var(--color-surface)',
                                                }}
                                            >
                                                {/* Page Preview */}
                                                <div style={{
                                                    aspectRatio: '8.5/11',
                                                    backgroundColor: 'var(--color-surface-elevated)',
                                                    borderRadius: 'var(--radius-md)',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    marginBottom: 'var(--spacing-3)',
                                                    position: 'relative',
                                                    overflow: 'hidden',
                                                    transform: `rotate(${page.rotation}deg)`,
                                                    transition: 'transform var(--transition-base)'
                                                }}>
                                                    <div style={{
                                                        fontSize: 'var(--text-2xl)',
                                                        fontWeight: 'var(--font-bold)',
                                                        color: isSelected ? 'var(--color-accent)' : 'var(--color-text-tertiary)'
                                                    }}>
                                                        {index + 1}
                                                    </div>

                                                    {/* Badges */}
                                                    {page.rotation !== 0 && (
                                                        <Badge
                                                            variant="warning"
                                                            style={{
                                                                position: 'absolute',
                                                                top: 'var(--spacing-2)',
                                                                right: 'var(--spacing-2)'
                                                            }}
                                                        >
                                                            {page.rotation}°
                                                        </Badge>
                                                    )}
                                                    {page.isDuplicate && (
                                                        <Badge
                                                            variant="info"
                                                            style={{
                                                                position: 'absolute',
                                                                top: 'var(--spacing-2)',
                                                                left: 'var(--spacing-2)'
                                                            }}
                                                        >
                                                            Copy
                                                        </Badge>
                                                    )}
                                                </div>

                                                {/* Page Info */}
                                                <div style={{
                                                    fontSize: 'var(--text-sm)',
                                                    fontWeight: 'var(--font-medium)',
                                                    textAlign: 'center',
                                                    marginBottom: 'var(--spacing-2)'
                                                }}>
                                                    Page {index + 1}
                                                </div>

                                                {/* Move Buttons */}
                                                <div style={{ display: 'flex', gap: 'var(--spacing-1)', justifyContent: 'center' }}>
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        icon
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            movePageUp(index);
                                                        }}
                                                        disabled={index === 0}
                                                    >
                                                        <ArrowUp size={14} />
                                                    </Button>
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        icon
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            movePageDown(index);
                                                        }}
                                                        disabled={index === pages.length - 1}
                                                    >
                                                        <ArrowDown size={14} />
                                                    </Button>
                                                </div>
                                            </Card>
                                        </div>
                                    );
                                })}
                            </div>
                        </Card>

                    {/* Progress Bar */}
                    {isProcessing && (
                        <ProgressBar value={progress} striped showLabel />
                    )}

                    {/* Preview and Apply Buttons */}
                    <div style={{ marginTop: 'var(--spacing-6)', display: 'flex', gap: 'var(--spacing-3)' }}>
                        {previewBlob && (
                            <Button
                                variant="secondary"
                                size="lg"
                                onClick={() => setShowPreview(true)}
                                style={{ flex: 1 }}
                            >
                                <FileText size={20} style={{ marginRight: 'var(--spacing-2)' }} />
                                View Preview
                            </Button>
                        )}
                        <Button
                            variant="primary"
                            size="lg"
                            onClick={applyChanges}
                            disabled={isProcessing}
                            loading={isProcessing}
                            style={{ flex: previewBlob ? 1 : 'none', width: previewBlob ? 'auto' : '100%' }}
                            fullWidth={!previewBlob}
                        >
                            <Download size={20} />
                            Apply Changes & Download
                        </Button>
                    </div>
                </>
            )}

            {/* PDF Preview Modal */}
            {previewBlob && showPreview && (
                <PDFPreview
                    pdfBlob={previewBlob}
                    filename={previewFilename}
                    onClose={() => setShowPreview(false)}
                    onDownload={() => {
                        if (previewBlob && previewFilename) {
                            saveAs(previewBlob, previewFilename);
                            toast.success('Download started!');
                        }
                    }}
                />
            )}
        </div>
    );
};

export default OrganizePDF;
